import React from "react";
import { useTranslation } from "react-i18next";
import tw from "twin.macro";
import styled from "styled-components";
import { motion } from "framer-motion";
import { SectionHeading } from "components/misc/Headings.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import BgTitle from "components/title/BgTitle";
import coWorkImg from "images/suppliers/co-working.svg";
import designerImg from "images/suppliers/designer.svg";
import friendShipImg from "images/suppliers/true-friends.svg";
import SuppliersMobileView from "./SuppliersMobileView";

const Row = tw.div`flex flex-col lg:flex-row mt-2`;
const Heading = tw(SectionHeading)`text-left text-xl`;

// Popular Posts
const PopularPostsContainer = tw.div`lg:w-2/3 mb-12 lg:mb-0`;
const PostsGrid = tw.div`mt-12 grid gap-8 grid-cols-1 sm:grid-cols-2 md:grid-cols-3`;
const Post = styled(motion.a)`
  ${tw`block bg-white rounded-lg shadow-md transition-transform duration-300 hover:scale-105`}
  border: 1px dashed #0c4d966b;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: #fafafa94;
`;
const Image = styled(motion.div)((props) => [
  `background-image: url("${props.$imageSrc}");`,
  tw`h-40 bg-cover bg-center rounded-lg mb-4`,
]);
const Title = tw.h5`text-sm font-bold transition duration-300 group-hover:text-ncc text-center`;
const Description = tw.p`mt-2 font-medium text-secondary-100 leading-relaxed text-sm text-center`;

// Recent Posts
const RecentPostsContainer = tw.div`lg:w-1/3 ml-16`;
const RecentPostsList = tw.ul`space-y-4`;
const ListItem = tw.li`text-gray-700 text-sm font-medium leading-relaxed border-l-4 border-blue-500 pl-4`;

export default ({ isXs = false }) => {
  if (isXs) return <SuppliersMobileView />;
  const { t } = useTranslation();
  const postBackgroundSizeAnimation = {
    rest: { backgroundSize: "100%" },
    hover: { backgroundSize: "110%" },
  };

  const salesLeasePosts = t("suppliers.popular_posts", { returnObjects: true });
  const recentPosts = t("suppliers.recent_posts", { returnObjects: true });

  const images = [designerImg, coWorkImg, friendShipImg];

  return (
    <div>
      <Container>
        <ContentWithPaddingXl>
          <BgTitle text={t("suppliers.title")} />
          <Row>
            {/* Popular Posts Section */}
            <PopularPostsContainer>
              <Heading>{t("suppliers.sales_and_lease_heading")}</Heading>
              <PostsGrid>
                {salesLeasePosts.map((post, index) => (
                  <Post
                    key={index}
                    className="group"
                    initial="rest"
                    whileHover="hover"
                    animate="rest"
                  >
                    <Image
                      transition={{ duration: 0.3 }}
                      variants={postBackgroundSizeAnimation}
                      $imageSrc={images[index]}
                    />
                    <Title>{post.title}</Title>
                    <Description>{post.description}</Description>
                  </Post>
                ))}
              </PostsGrid>
            </PopularPostsContainer>

            {/* Recent Posts Section */}
            <RecentPostsContainer>
              <Heading>{t("suppliers.ses_heading")}</Heading>
              <RecentPostsList>
                {recentPosts.map((post, index) => (
                  <ListItem key={index}>{post}</ListItem>
                ))}
              </RecentPostsList>
            </RecentPostsContainer>
          </Row>
        </ContentWithPaddingXl>
      </Container>
    </div>
  );
};
